import type { TopLevelNavigationEnabled } from '~/components/shared/header/topLevelNavigation/hooks/useTopLevelNavigationEnabled';
import type { VendorEvent } from '~/types/global';

/**
 * `experience_impression` payloads for the CRO experiment TK69 (top level navigation). The
 * experiment is not split by Kameleoon: what the site renders decides the variant, and the CRO
 * Tableau reads these exact values, so they are hardcoded rather than derived.
 */
const TOP_NAVIGATION_EXPERIENCE_IMPRESSIONS = {
  /** Side navigation */
  control: {
    event: 'experience_impression',
    exp_variant_string: 'Server-TK69-0',
    ss_experiment_id: 'TK69',
    ss_experiment_name: 'TK 69 Top Nav',
    ss_variant_id: '0',
    ss_variant_name: 'Control',
  },
  /** Top level navigation */
  variant: {
    event: 'experience_impression',
    exp_variant_string: 'Server-TK69-1',
    ss_experiment_id: 'TK69',
    ss_experiment_name: 'TK 69 Top Nav',
    ss_variant_id: '1',
    ss_variant_name: 'Variant',
  },
} as const;

export type TopNavigationExperimentVariant = keyof typeof TOP_NAVIGATION_EXPERIENCE_IMPRESSIONS;

/**
 * Which TK69 variant the site serves, from whether it renders the TLN on desktop and on mobile
 * (`getTopLevelNavigationEnabled`: switch on plus renderable content; a switch on without content
 * still shows the side navigation, so it counts as control). Decided per site, not per device: a
 * site with the TLN on desktop only (or mobile only) is in the variant for every visitor.
 */
export const getTopNavigationExperimentVariant = ({
  desktop,
  mobile,
}: TopLevelNavigationEnabled): TopNavigationExperimentVariant => {
  const anyTopNavigationEnabled = desktop || mobile;

  if (anyTopNavigationEnabled) {
    return 'variant';
  }

  return 'control';
};

/**
 * The TK69 `experience_impression` event for the site, or `null` when the site is not part of the
 * experiment (`experienceImpressionEventEnabled` off).
 */
export const parseTopNavigationExperienceImpression = (
  experienceImpressionEventEnabled: boolean | null | undefined,
  topNavigationRendered: TopLevelNavigationEnabled
): VendorEvent | null => {
  if (!experienceImpressionEventEnabled) {
    return null;
  }

  // a fresh object per push: GTM writes `gtm.uniqueEventId` onto every object pushed to dataLayer
  return {
    ...TOP_NAVIGATION_EXPERIENCE_IMPRESSIONS[
      getTopNavigationExperimentVariant(topNavigationRendered)
    ],
  };
};
